import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { menuItems, menuCategories } from '@/mocks/menu';
import { useCart } from '@/context/CartContext';

export default function Menu() {
  const { t } = useTranslation();
  const { addItem, openCart } = useCart();
  const [active, setActive] = useState('all');
  const [addedIds, setAddedIds] = useState<Record<string, boolean>>({});

  const filtered = active === 'all' ? menuItems : menuItems.filter((m) => m.category === active);

  const catLabel = (id: string) => t(`menu.cats.${id}`);

  const handleAdd = (itemId: string) => {
    const item = menuItems.find((m) => m.id === itemId);
    if (!item) return;
    addItem(item);
    setAddedIds((prev) => ({ ...prev, [itemId]: true }));
    setTimeout(() => {
      setAddedIds((prev) => ({ ...prev, [itemId]: false }));
    }, 1200);
  };

  return (
    <section id="menu" className="w-full bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center justify-center gap-2 text-xs font-semibold tracking-[0.3em] uppercase text-primary-600">
            <span className="h-px w-8 bg-primary-500"></span>
            {t('menu.eyebrow')}
            <span className="h-px w-8 bg-primary-500"></span>
          </span>
          <h2 className="mt-4 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
            {t('menu.title')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">
            {t('menu.subtitle')}
          </p>
        </div>

        <div className="mt-9 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setActive('all')}
            className={`rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
              active === 'all'
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-200/70 text-foreground-700 hover:bg-background-300/60'
            }`}
          >
            {t('menu.all')}
          </button>
          {menuCategories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActive(cat.id)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                active === cat.id
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-200/70 text-foreground-700 hover:bg-background-300/60'
              }`}
            >
              {catLabel(cat.id)}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => {
            const name = t(`menu.items.${item.id}.name`);
            const desc = t(`menu.items.${item.id}.desc`);
            const tag = t(`menu.items.${item.id}.tag`);
            const added = addedIds[item.id];
            return (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-lg bg-background-50"
              >
                <div className="relative h-56 w-full overflow-hidden">
                  <img
                    src={item.image}
                    alt={name}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  {tag && (
                    <span className="absolute left-4 top-4 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                      {tag}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-lg font-semibold text-foreground-950">{name}</h3>
                    <span className="whitespace-nowrap font-semibold text-primary-600">{item.price}</span>
                  </div>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">{desc}</p>
                  <button
                    type="button"
                    onClick={() => handleAdd(item.id)}
                    className={`mt-4 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                      added
                        ? 'bg-secondary-500 text-background-50'
                        : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                    }`}
                  >
                    {added ? (
                      <>
                        <i className="ri-check-line"></i>
                        {t('menu.added')}
                      </>
                    ) : (
                      <>
                        <i className="ri-add-line"></i>
                        {t('menu.add')}
                      </>
                    )}
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <button
            type="button"
            onClick={openCart}
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
          >
            <i className="ri-shopping-bag-3-line"></i>
            {t('menu.request')}
          </button>
        </div>
      </div>
    </section>
  );
}